import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getWithdrawalsByRealtor } from '../../../services/withdrawalService';

const STATUS_MAP = {
  requested: '대기',
  approved: '승인',
  completed: '완료',
  rejected: '반려',
};

const WithdrawHistory = () => {
  const { userData } = useAuth();
  const uid = userData?.uid;

  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const data = await getWithdrawalsByRealtor(uid, year, month);
      setList(data);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      <FilterRow>
        <SmallSelect value={year} onChange={(e) => setYear(e.target.value)}>
          {[2026, 2025, 2024].map((y) => (
            <option key={y} value={String(y)}>{y} 년</option>
          ))}
        </SmallSelect>
        <SmallSelect value={month} onChange={(e) => setMonth(e.target.value)}>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={String(i + 1)}>{i + 1} 월</option>
          ))}
        </SmallSelect>
        <SearchBtn onClick={fetchData} disabled={loading}>검색</SearchBtn>
      </FilterRow>

      <TableHeader>
        <TableCol>출금신청일</TableCol>
        <TableCol>출금신청액</TableCol>
        <TableCol>상태</TableCol>
        <TableCol>출금계좌</TableCol>
      </TableHeader>

      {loading ? (
        <EmptyBox><EmptyTitle>조회 중...</EmptyTitle></EmptyBox>
      ) : list.length === 0 ? (
        <EmptyBox>
          <EmptyIcon>!</EmptyIcon>
          <EmptyTitle>출금 내역이 없습니다</EmptyTitle>
        </EmptyBox>
      ) : (
        list.map((item) => (
          <TableRow key={item.id}>
            <TableCell>{item.createdAt}</TableCell>
            <TableCell>{(item.amount || 0).toLocaleString()}원</TableCell>
            <TableCell>{STATUS_MAP[item.status] || item.status}</TableCell>
            <TableCell>{item.bankName} {item.accountNumber?.slice(-4)}</TableCell>
          </TableRow>
        ))
      )}
    </Container>
  );
};

export default WithdrawHistory;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const SmallSelect = styled.select`
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.bgWhite};
`;

const SearchBtn = styled.button`
  height: 40px;
  padding: 0 ${({ theme }) => theme.spacing.base};
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:disabled { opacity: 0.6; }
`;

const TableHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const TableCol = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

const TableRow = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TableCell = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;
